import React from 'react';

import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import styled, {css} from 'styled-components';

import {FolderActionsButton} from 'app/components/general/FolderActionsButton';
import {FullHTMLLink} from 'app/components/general/FullHTMLLink';
import {GenericPaginatedListRTK} from 'app/components/general/lists/GenericPaginatedListRTK';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {useCurrentUserState} from 'app/current-user/api';
import {getCurrentUserPerms} from 'app/current-user/utils';
import {useDocuments} from 'app/documents/api';
import {useFolderByHashid, useFolderCounts} from 'app/folders/api';
import {StyledListRowDIV} from 'app/general/bootstrap/utils';
import {ItemRelationsListOfGenerics} from 'app/general/dashboards/ItemRelationsListOfGenerics';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {UserContentContainer} from 'app/general/structures/UserContentContainer';
import {getMediaImage} from 'app/general/structures/UserContentContainer/nodeProcessors/MakeImagesZoomable/UserContentImage';
import {KBTreeDeleteItemContextProvider} from 'app/kbs/components/KBEditPage/KBTree/KBTreeDeleteItemModal';
import {useAccountSettingsState} from 'app/settings/api';
import {sweetAPI} from 'app/store/sweet-api';
import {DOCUMENT_CREATE_PAGE_URL, getDashboardFolderDetailURL} from 'app/urls';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import {getObjectName} from 'app/utils/fallbacks';
import {removeMediaPath} from 'app/utils/fineuploader';
import {gt, ngt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';
import {DOCUMENT_TAG} from 'app/utils/rtk-tags';

import {DocumentWidget} from './DocumentWidget';
import {useCurrentFolder} from './FolderContext';
import {FoldersList} from './HomeFoldersDashboard';

import {FolderHashidSerializedType, FolderSerializedType} from 'sweet-types';

import type {BreadcrumbProps} from 'app/general/structures/BreadcrumbItemStructure';
import type {WithRouterProps} from 'react-router';

type Props = WithRouterProps<{hashid: string}>;

export function HomeFolderDetailDashboard(props: Props) {
  return (
    <div className="container mt-4">
      <FolderHero hashid={props.params.hashid} />
      <FoldersList parent={props.params.hashid} />
      <DocumentsList folder={props.params.hashid} />
    </div>
  );
}

interface ListProps {
  folder: string;
}
export function DocumentsList({folder}: ListProps) {
  const actions = useAsyncActions({
    invalidateTags: sweetAPI.util.invalidateTags,
  });

  const {isTeamMember} = getCurrentUserPerms(useCurrentUserState().data!);

  function refetchDocs() {
    // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
    actions.invalidateTags([{type: DOCUMENT_TAG, id: 'list'}]);
    return Promise.resolve(true);
  }

  return (
    <div className="py-4">
      <h4
        id="documents"
        className="border-bottom pb-2 mb-4 text-muted text-uppercase"
      >
        {gt('Documents')}
      </h4>
      <GenericPaginatedListRTK
        WrapperComponent={StyledListRowDIV}
        ItemComponent={DocumentWidget}
        useQuery={useDocuments}
        noItemsMessage={() => (
          <NoResultsMessage head={gt('No documents found.')}>
            {isTeamMember ? (
              <FullHTMLLink
                className="btn btn-primary btn-lg"
                html_url={
                  DOCUMENT_CREATE_PAGE_URL +
                  (folder !== '' ? `?folder=${folder}` : '')
                }
              >
                {gt('Create a Document')}
              </FullHTMLLink>
            ) : null}
          </NoResultsMessage>
        )}
        paramsBuilder={() => ({
          folder,
          page_size: DEFAULT_PAGE_SIZE,
        })}
        customProps={() => {
          return {
            refetchDocs,
          };
        }}
      />
    </div>
  );
}

interface NestedFolderBreadcrumbProps extends BreadcrumbProps {
  params: {hashid: string};
}

function buildBreadcrumbPath(
  root: FolderHashidSerializedType | null,
  seen: Set<string> = new Set()
): Array<FolderHashidSerializedType> {
  if (!root) {
    return [];
  }
  if (seen.has(root.hashid)) {
    return [];
  }
  seen.add(root.hashid);
  const ancestors = buildBreadcrumbPath(root.parent, seen);
  return [...ancestors, root];
}

export function NestedFolderBreadcrumb({
  params,
  ...props
}: NestedFolderBreadcrumbProps) {
  const {data: folder, isLoading} = useFolderByHashid(params.hashid, {
    refetchOnMountOrArgChange: false,
  });
  const items = React.useMemo(
    () => buildBreadcrumbPath(folder ?? null),
    [folder]
  );

  if (isLoading) {
    return null;
  }

  return (
    <>
      {items.map(function (node, idx) {
        const isCurrent = idx === items.length - 1;
        return (
          <BreadcrumbItemStructure
            key={node.hashid}
            {...props}
            icon={cs('far', {
              'fa-folder-open': isCurrent,
              'fa-folder': !isCurrent,
            })}
            text={node.name}
            active={isCurrent}
            shrinkText={true}
            link={getDashboardFolderDetailURL(node.name, node.hashid)}
          />
        );
      })}
    </>
  );
}

interface HeroMetaProps {
  hashid: string;
}

function FolderHero({hashid}: HeroMetaProps) {
  const {folder, refetch} = useCurrentFolder();
  const settings = useAccountSettingsState().data!;

  if (!folder) {
    return null;
  }

  const heroBg = folder.description.images[0]?.image_ref
    ? removeMediaPath(
        folder.description.images[0].image_ref,
        settings.MEDIA_URL
      )
    : undefined;

  const [src] = getMediaImage(
    heroBg,
    '',
    settings.MEDIA_URL_READ,
    settings.MEDIA_URL,
    heroBg,
    null,
    'original'
  );

  return (
    <KBTreeDeleteItemContextProvider>
      <FolderHeroDiv className="jumbotron p-4 mb-4" heroBg={src}>
        <div className="d-flex align-items-center justify-content-between">
          <HeadingWrapper
            className="d-flex align-items-center"
            isOffset={!!src}
          >
            <FolderIcon className={cs('text-light mr-4', {'d-none': src})}>
              <i className="fas fa-folder-open" />
            </FolderIcon>
            <div>
              <h2>{getObjectName(folder, 'folder', true)}</h2>
              <ItemRelationsListOfGenerics
                items={folder.teams.map((team) => ({
                  name: team.name,
                  html_url: team.html_url,
                }))}
                contentType={'team'}
              />
              <div className="mt-2">
                <UserContentContainer content={folder.description.content} />
              </div>
            </div>
          </HeadingWrapper>
          <FolderActionsButton
            object={folder as FolderSerializedType}
            allowedTeams={folder.parent?.teams ?? []}
            refreshData={() => {
              refetch();
              return Promise.resolve(true);
            }}
          />
        </div>
        <FolderHeroMeta hashid={hashid} />
      </FolderHeroDiv>
    </KBTreeDeleteItemContextProvider>
  );
}

function FolderHeroMeta({hashid}: HeroMetaProps) {
  const {data} = useFolderCounts(hashid);
  if (!data) {
    return null;
  }
  return (
    <div className="text-muted text-right">
      <a className="badge badge-light mr-3" href="#folders">
        {ngt('%s Folder', '%s Folders', data.num_folders)}
      </a>
      <a className="badge badge-light" href="#documents">
        {ngt('%s Document', '%s Documents', data.num_documents)}
      </a>
    </div>
  );
}

interface HeroDivProps {
  heroBg?: string;
}

const FolderHeroDiv = styled.div<HeroDivProps>`
  &.jumbotron {
    ${({heroBg}) =>
      heroBg
        ? css`
            background-color: ${colors.veryLightBg};
            background-image: linear-gradient(
                to right,
                rgba(255, 255, 255, 0) 0%,
                ${colors.veryLightBg} 20%,
                ${colors.veryLightBg} 40%
              ),
              url(${heroBg});
            background-size: contain;
            background-repeat: no-repeat;
            background-position: left center, left top;
          `
        : css`
            background-color: ${colors.veryLightBg};
          `};
  }
`;

const FolderIcon = styled.div`
  font-size: 5rem;
`;

interface HeadingWrapperProps {
  isOffset?: boolean;
}

const HeadingWrapper = styled.div<HeadingWrapperProps>`
  ${({isOffset}) =>
    isOffset &&
    css`
      margin-left: 15%;
    `}
`;
